/**
 * The phone's two bars — §8 of the record page plan.
 *
 * **Quick actions** sit under the identity: four equal buttons, the first
 * three fixed per object (a contact's are Call, Text, Email) and the fourth
 * green and about the tab you are on — Log activity on Overview, New job on
 * Jobs, Upload on Files. The fixed three are the things somebody standing in
 * front of a customer does; the fourth is the thing somebody looking at *this
 * tab* does, which is why it moves.
 *
 * **The tab bar** is fixed to the bottom: four tabs and More. It is the same
 * `RecordTab[]` the desktop row draws, so a tab cannot exist on one and not
 * the other — More holds whatever did not get a cell, with its count.
 */
import { useState, type ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { useDismiss } from '../components/overlays.js'
import { useRef } from 'react'
import { COLUMN_ICONS, IconClose, IconMore, NAV_ICONS, RECORD_TAB_ICONS } from '../frame/icons.js'
import type { FieldIcon } from '@rowted/shared'
import type { RecordTab } from './record-tabs.js'

export interface QuickAction {
  label: string
  icon: ReactNode
  /** `tel:` / `sms:` / `mailto:` / a maps URL, or a handler. */
  href?: string
  onClick?: () => void
}

export function PhoneQuickActions({
  actions, green,
}: {
  actions: QuickAction[]
  green: QuickAction | null
}) {
  const all = green ? [...actions.slice(0, 3), green] : actions.slice(0, 4)

  return (
    <div className="flex shrink-0 gap-2 border-b border-line bg-surface px-4 pb-3">
      {all.map((action, index) => {
        const isGreen = green !== null && index === all.length - 1
        const className = cn(
          'flex flex-1 flex-col items-center justify-center gap-1 rounded-touch py-2.5',
          'text-[11.5px] font-semibold',
          isGreen
            ? 'bg-accent text-white'
            : 'border border-line bg-surface text-text',
        )
        const inner = (
          <>
            <span className={isGreen ? 'text-white' : 'text-text-muted'}>{action.icon}</span>
            <span className="truncate px-1">{action.label}</span>
          </>
        )

        return action.href
          ? <a key={action.label} href={action.href} className={className}>{inner}</a>
          : (
            <button key={action.label} type="button" onClick={action.onClick} className={className}>
              {inner}
            </button>
          )
      })}
    </div>
  )
}

export function PhoneTabBar({
  tabs, cells, active, onChange, objectLabel, more: offerMore = true, counts = false, label = 'Record', moreSheet,
}: {
  tabs: RecordTab[]
  /** Which tab keys get a cell of their own. The rest go in More. */
  cells: string[]
  active: string
  onChange: (key: string) => void
  objectLabel: string
  /**
   * The More cell and its sheet. A record page always has one; Activity's
   * four tabs each have a cell (Aaron, Oct 25 phone note 2), so it passes
   * `false` and nothing is left to hold.
   */
  more?: boolean
  /**
   * Each cell's `count` as a badge over its icon, in the nav's accent — Activity's
   * Inbox unread and Tasks due or late. Record pages draw none, as before.
   */
  counts?: boolean
  /** What the bar is called — a record page's is *Record*. */
  label?: string
  /**
   * The visit page's More sheet (folder 50, Board 16 E) — opt-in, additive
   * (lead decision 13): an 18 px top, the label at the left over the rows, a
   * 36 px tile per tab and its count at the right. Left out, More is today's.
   */
  moreSheet?: 'list'
}) {
  const [more, setMore] = useState(false)
  const sheet = useRef<HTMLDivElement>(null)
  useDismiss(more, () => setMore(false), sheet)

  const shown = cells
    .map((key) => tabs.find((tab) => tab.key === key))
    .filter((tab): tab is RecordTab => tab !== undefined)
  const rest = tabs.filter((tab) => !cells.includes(tab.key))

  /*
   * More is "active" whenever the open tab is one of the ones it holds — so
   * somebody who opened Files from the sheet can see, from the bar, where
   * they are. §8: "the bar shows More as active while that tab is open".
   */
  const restIsOpen = rest.some((tab) => tab.key === active)

  return (
    <>
      {more && moreSheet === 'list' && (
        <div className="fixed inset-0 z-40 bg-[rgba(29,29,26,0.35)]">
          <div
            ref={sheet}
            role="dialog"
            aria-label={`More on this ${objectLabel}`}
            className="absolute inset-x-0 bottom-[80px] rounded-t-sheet bg-surface pb-2.5 pt-2 leading-[normal] shadow-[0_-10px_30px_rgba(20,20,10,0.14)]"
          >
            <div className="flex justify-center pb-2 pt-1">
              <i aria-hidden className="block h-1 w-9 rounded-full bg-line-strong" />
            </div>
            <div className="px-4 pb-1.5 text-[12px] font-semibold uppercase tracking-[.05em] text-text-faint">
              More on this {objectLabel}
            </div>
            {rest.map((tab) => {
              const Icon = iconFor(tab.icon)
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => { onChange(tab.key); setMore(false) }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-hover text-text">
                    {Icon ? <Icon size={18} /> : null}
                  </span>
                  <b className="flex-1 text-[15px] font-semibold text-text">{tab.label}</b>
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="tnum rounded-chip bg-line-soft px-[7px] py-px text-[11px] font-semibold text-text-muted">
                      {tab.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
      {more && moreSheet !== 'list' && (
        <div className="fixed inset-0 z-40 bg-[rgba(29,29,26,0.35)]">
          <div
            ref={sheet}
            role="dialog"
            aria-label={`More on this ${objectLabel}`}
            className="absolute inset-x-0 bottom-[80px] rounded-t-record border-t border-line bg-surface pb-2"
          >
            <div className="flex flex-col items-center gap-2 pb-1 pt-2">
              <span aria-hidden className="h-1 w-9 rounded-chip bg-line-strong" />
              <span className="text-label">More on this {objectLabel}</span>
            </div>
            {rest.map((tab) => {
              const Icon = iconFor(tab.icon)
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => { onChange(tab.key); setMore(false) }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-field bg-line-soft text-text-muted">
                    {Icon ? <Icon size={18} /> : null}
                  </span>
                  <span className="text-[15px] font-semibold text-text">{tab.label}</span>
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="tnum ml-auto rounded-chip bg-line-soft px-2 py-px text-[11px] font-semibold text-text-muted">
                      {tab.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/*
        * The device's own inset under the bar: a real iPhone gives its home
        * indicator's room (`env(safe-area-inset-bottom)`), a desktop browser 0.
        * `--device-inset-bottom` lets the board diff emulate the device a
        * board draws (Board 16's 22 px home-indicator strip) — nothing in the
        * app sets it, so it ships no fixed gap (lead, Oct 29).
        */}
      <nav
        aria-label={label}
        className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface pb-[max(var(--device-inset-bottom,0px),env(safe-area-inset-bottom))]"
      >
        <div className="flex h-[58px] items-center">
          {shown.map((tab) => (
            <TabCell
              key={tab.key}
              tab={tab}
              on={tab.key === active && !more}
              onClick={() => onChange(tab.key)}
              count={counts ? tab.count : undefined}
            />
          ))}
          {offerMore && <TabCell
            tab={{
              key: '__more',
              label: more ? 'Close' : 'More',
              count: undefined,
            }}
            icon={more ? <IconClose size={20} /> : <IconMore size={20} />}
            on={more || restIsOpen}
            onClick={() => setMore((open) => !open)}
          />}
        </div>
      </nav>
    </>
  )
}

function TabCell({
  tab, on, onClick, icon, count,
}: {
  tab: RecordTab
  on: boolean
  onClick: () => void
  icon?: ReactNode
  /** A badge over the icon's corner — only where the bar was asked for `counts`. */
  count?: number
}) {
  const Icon = iconFor(tab.icon)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={on ? 'page' : undefined}
      className={cn('flex flex-1 flex-col items-center justify-center gap-[3px] py-1', count !== undefined && 'relative')}
    >
      <span
        className={cn(
          'flex h-7 w-11 items-center justify-center rounded-field',
          on ? 'bg-accent-soft text-accent' : 'text-text-muted',
        )}
      >
        {icon ?? (Icon ? <Icon size={20} /> : null)}
      </span>
      <span className={cn('text-[10.5px]', on ? 'font-bold text-accent' : 'font-medium text-text-muted')}>
        {tab.label}
      </span>
      {/*
        * The nav's accent badge (Board 1: 10.5/700, white on the accent), over
        * the icon's top-right corner. After the label in the markup, so the
        * cell reads *Inbox 3* like the desktop's tab.
        */}
      {count !== undefined && count > 0 && (
        <span data-tab-count className="tnum absolute left-1/2 top-[3px] ml-2.5 min-w-[18px] rounded-chip bg-accent px-[5px] py-px text-center text-tag font-bold leading-[14px] text-white ring-2 ring-surface">
          {count}
        </span>
      )}
    </button>
  )
}

function iconFor(name: RecordTab['icon']) {
  if (!name) return null
  if (name in RECORD_TAB_ICONS) return RECORD_TAB_ICONS[name as keyof typeof RECORD_TAB_ICONS]
  if (name in NAV_ICONS) return NAV_ICONS[name as keyof typeof NAV_ICONS]
  return COLUMN_ICONS[name as FieldIcon] ?? null
}
